import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from 'urql';
import {
  AutomationForm,
  FormValues,
} from '~/components/automation/AutomationForm/AutomationForm';
import {
  getTemplateBehavior,
  isAssetDrivenTemplate,
} from '~/components/job/AssetDrivenTemplateIndicator';
import { Dialog } from '~/components/ui/Dialog';
import { getFragmentData, graphql } from '~/gql';
import { JobTemplateFields } from '~/graphql/fragment/JobTemplateFields';
import { UpdateJobTemplateDocument } from '~/graphql/mutations/job-template/UpdateJobTemplate';
import { handleTemplateAction } from '~/helpers/automationTemplateHelpers';
import { AutomationFormDocument, formToInput } from './automations.new';

const UpdateAutomationDocument = graphql(`
  mutation UpdateAutomation($id: ID!, $input: AutomationInput!) {
    updateAutomation(id: $id, input: $input)
  }
`);

const CreateJobTemplateDocument = graphql(`
  mutation CreateJobTemplate($input: JobTemplateInput!) {
    createJobTemplate(input: $input) {
      id
    }
  }
`);

export function Component() {
  const navigate = useNavigate();
  const params = useParams();

  const [, updateAutomation] = useMutation(UpdateAutomationDocument);
  const [, updateJobTemplate] = useMutation(UpdateJobTemplateDocument);
  const [, createJobTemplate] = useMutation(CreateJobTemplateDocument);
  const [result] = useQuery({
    query: AutomationFormDocument,
    variables: { id: params.automationId },
    requestPolicy: 'network-only',
  });

  // CRITICAL FIX: Independently fetch templates to get correct includeAttributes
  // The nested automation.template.includeAttributes comes back as null due to backend issue
  // where JobTemplateRepository.findById doesn't call toResponseType.
  // This matches the pattern used in automations.$automationId.tsx (view route).
  // Use network-only to avoid cached templates without includeAttributes/jobAttributeIds fields
  const [templateResult] = useQuery({
    query: graphql(`
      query GetAllJobTemplates {
        jobTemplates(status: Active) {
          ...JobTemplateFields
        }
      }
    `),
    requestPolicy: 'network-only',
  });

  const [showTemplateActionDialog, setShowTemplateActionDialog] =
    useState(false);
  const [pendingFormData, setPendingFormData] = useState<FormValues | null>(
    null
  );
  const [showCreateTemplateDialog, setShowCreateTemplateDialog] =
    useState(false);
  const [isUpdatingAfterTemplateCreation, setIsUpdatingAfterTemplateCreation] =
    useState(false);
  const [isUpdatingAfterTemplateUpdate, setIsUpdatingAfterTemplateUpdate] =
    useState(false);

  // Tracks the last template state for which the user has already chosen
  // how to handle changes (update existing vs create new). This prevents
  // the template action dialog from reappearing for the same templateData.
  const lastTemplateFingerprintRef = useRef<string | null>(null);

  const getTemplateFingerprint = (
    formData: FormValues | null | undefined
  ): string | null => {
    if (!formData?.templateData) return null;
    try {
      // Only fingerprint templateData; other automation fields are irrelevant
      // to the question of whether the template has changed.
      return JSON.stringify(formData.templateData);
    } catch {
      return null;
    }
  };

  // Reset all state when automationId changes (navigating between different automations)
  useEffect(() => {
    setShowTemplateActionDialog(false);
    setPendingFormData(null);
    setShowCreateTemplateDialog(false);
    setIsUpdatingAfterTemplateCreation(false);
    setIsUpdatingAfterTemplateUpdate(false);
    lastTemplateFingerprintRef.current = null;
  }, [params.automationId]);

  // Memoize templates mapping to avoid remapping on every action and prevent stale closures
  const templates = useMemo(() => {
    if (!result.data?.jobTemplates) return [];
    return result.data.jobTemplates.map((template) =>
      getFragmentData(JobTemplateFields, template)
    );
  }, [result.data?.jobTemplates]);

  // Merge the independently-fetched templates with the automation data
  // This ensures includeAttributes is correct even when the nested automation.template
  // returns null due to the backend toResponseType issue
  const automationWithCorrectTemplate = useMemo(() => {
    if (!result.data?.automation) return null;

    const automation = result.data.automation;
    if (!templateResult.data?.jobTemplates || !automation.template) {
      return automation;
    }

    const templatesFromList = templateResult.data.jobTemplates.map((template) =>
      getFragmentData(JobTemplateFields, template)
    );

    const correctTemplate = templatesFromList.find(
      (t) => t.id === automation.template.id
    );

    if (!correctTemplate) {
      console.warn('[Edit Route] Template not found in independent query');
      return automation;
    }

    // CRITICAL: Only merge if correctTemplate actually has the includeAttributes/jobAttributeIds fields
    // Until codegen runs with the updated fragment, correctTemplate will have undefined for these fields
    // and merging would overwrite the correct values from automation.template
    const hasAttributeFields =
      'includeAttributes' in correctTemplate ||
      'jobAttributeIds' in correctTemplate;

    if (!hasAttributeFields) {
      console.warn(
        '[Edit Route] ⚠️  JobTemplateFields fragment missing attribute fields - using automation.template directly'
      );
      return automation;
    }

    // Merge: preserve nested template task data but use correct includeAttributes from independent query
    return {
      ...automation,
      template: {
        ...automation.template,
        includeAttributes:
          correctTemplate.includeAttributes !== undefined
            ? correctTemplate.includeAttributes
            : automation.template.includeAttributes,
        jobAttributeIds:
          correctTemplate.jobAttributeIds !== undefined
            ? correctTemplate.jobAttributeIds
            : automation.template.jobAttributeIds,
        // Add other fields that might be null in nested query
      },
    };
  }, [result.data?.automation, templateResult.data?.jobTemplates]);

  // NOTE: We no longer need to store originalTemplate here because AutomationForm
  // now computes the templateModified flag and passes it up in the form data.
  // This avoids baseline mismatches between form and route.

  // Check if the selected template is asset-driven
  // IMPORTANT: These hooks must be called before any early returns to avoid hook count mismatch
  const selectedTemplate = useMemo(() => {
    if (!pendingFormData?.template) return null;
    return templates.find((t) => t.id === pendingFormData.template);
  }, [pendingFormData?.template, templates]);

  const isAssetDriven = useMemo(() => {
    if (!selectedTemplate) return false;
    return isAssetDrivenTemplate(getTemplateBehavior(selectedTemplate));
  }, [selectedTemplate]);

  if (!result.data) {
    return null;
  }

  if (!params.automationId) {
    return null;
  }

  // Wait for template query to complete before rendering form
  // This ensures originalTemplate has correct includeAttributes for comparison
  if (templateResult.fetching && result.data.automation?.template) {
    return null;
  }

  const handleTemplateActionWrapper = async (
    action: 'update' | 'create',
    formData: FormValues
  ): Promise<FormValues | null> => {
    if (!result.data) return null;

    const actionResult = await handleTemplateAction({
      action,
      formData,
      jobTemplates: templates,
      updateJobTemplate,
      createJobTemplate,
    });

    // Return formData only on success, null on failure
    return actionResult.success ? actionResult.formData : null;
  };

  const handleSubmitWithTemplateCheck = async (data: FormValues) => {
    if (!params.automationId || !result.data) {
      return;
    }

    // Skip template check if we're currently in the template creation/update flow
    // This prevents the dialog from showing again after template operations
    if (isUpdatingAfterTemplateCreation || isUpdatingAfterTemplateUpdate) {
      // Directly proceed with automation update
      const convertedInput = formToInput(data);
      const res = await updateAutomation({
        id: params.automationId,
        input: convertedInput,
      });

      if (res.error) {
        toast.error(
          `Failed to update automation: ${res.error.message || 'Unknown error'}`
        );
        // Reset flags to re-enable prompt on error
        setIsUpdatingAfterTemplateCreation(false);
        setIsUpdatingAfterTemplateUpdate(false);
        return;
      }

      const id = res.data?.updateAutomation;
      if (id) {
        return navigate(`/automations/${id}`);
      }
      // Reset flags to re-enable prompt when no ID is returned
      setIsUpdatingAfterTemplateCreation(false);
      setIsUpdatingAfterTemplateUpdate(false);
      return;
    }

    // Check if template was modified - use the flag computed by AutomationForm
    // to avoid baseline mismatches between form and route

    if (data.templateModified && data.templateData && data.template) {
      // Compute a fingerprint for the current templateData so we can avoid
      // re-showing the template action dialog for the exact same state.
      const fingerprint = getTemplateFingerprint(data);

      if (
        fingerprint &&
        lastTemplateFingerprintRef.current &&
        fingerprint === lastTemplateFingerprintRef.current
      ) {
        // We've already handled this exact template change (user picked
        // update/create previously), so skip the dialog and fall through
        // to the normal automation update logic.
      } else {
        // Template was modified and we haven't handled this state yet;
        // show the dialog and wait for the user's choice.
        setPendingFormData(data);
        setShowTemplateActionDialog(true);
        return; // Don't submit yet, wait for user choice
      }
    }

    // No template changes, proceed normally
    const convertedInput = formToInput(data);

    const res = await updateAutomation({
      id: params.automationId,
      input: convertedInput,
    });

    if (res.error) {
      toast.error(
        `Failed to update automation: ${res.error.message || 'Unknown error'}`
      );
      return; // Stop here if there's an error
    }

    const id = res.data?.updateAutomation;
    if (id) {
      return navigate(`/automations/${id}`);
    }
  };

  const handleCreateTemplate = (
    getFormValues: () => FormValues,
    openCreateDialog: () => void
  ) => {
    if (!result.data) {
      openCreateDialog();
      return;
    }

    // Skip template check if we're currently in the template creation/update flow
    if (isUpdatingAfterTemplateCreation || isUpdatingAfterTemplateUpdate) {
      openCreateDialog();
      return;
    }

    const currentFormData = getFormValues();

    // Check if template was modified - use the flag computed by AutomationForm

    if (
      currentFormData.templateModified &&
      currentFormData.templateData &&
      currentFormData.template
    ) {
      // Template was modified, show template action dialog
      setPendingFormData(currentFormData);
      setShowTemplateActionDialog(true);
      return;
    }

    // Template not modified, open create dialog directly
    openCreateDialog();
  };

  const handleTemplateCreated = async (
    newTemplateId: string,
    formData: FormValues
  ) => {
    if (!params.automationId) return;

    // Set flags to disable Prompt and bypass template check during update
    setIsUpdatingAfterTemplateCreation(true);

    // Clear pending form data AND close dialogs FIRST to prevent template action dialog from showing
    // This must happen before any async operations
    const pendingData = pendingFormData;
    setPendingFormData(null);
    setShowTemplateActionDialog(false);
    setShowCreateTemplateDialog(false);

    // If we have pending form data (from template action dialog), use it
    // Otherwise use the current form data
    const dataToUse = pendingData || formData;

    // Update form data with new template ID
    const updatedData: FormValues = {
      ...dataToUse,
      template: newTemplateId,
    };

    // Remember this template state so we don't re-open the template action
    // dialog for the same template changes on subsequent saves.
    lastTemplateFingerprintRef.current = getTemplateFingerprint(updatedData);

    // Update automation
    const convertedInput = formToInput(updatedData);
    const res = await updateAutomation({
      id: params.automationId,
      input: convertedInput,
    });

    if (res.error) {
      toast.error(
        `Failed to update automation: ${res.error.message || 'Unknown error'}`
      );
      setIsUpdatingAfterTemplateCreation(false); // Reset flag on error
      return;
    }

    const id = res.data?.updateAutomation;
    if (id) {
      // Navigate - Prompt should be disabled due to isUpdatingAfterTemplateCreation flag
      // Don't reset flag here - let useEffect handle it on navigation
      navigate(`/automations/${id}`);
    } else {
      // No ID returned, reset flag to re-enable prompt
      setIsUpdatingAfterTemplateCreation(false);
    }
  };

  return (
    <>
      {/* Template Action Dialog */}
      <Dialog
        show={showTemplateActionDialog}
        onClose={() => {
          setShowTemplateActionDialog(false);
          setPendingFormData(null);
        }}
        title='Template Changes Detected'
      >
        <div className='p-4'>
          {isAssetDriven ? (
            <>
              <p className='mb-4 text-sm text-grey-50'>
                Asset-driven templates cannot be modified. We will create a new
                Job template with your current selection.
              </p>
              <div className='m-4 text-right'>
                <button
                  type='button'
                  onClick={() => {
                    setShowTemplateActionDialog(false);
                    setPendingFormData(null);
                  }}
                  className='text-xs uppercase text-grey-50 transition-all duration-300 hover:text-grey-70'
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={async () => {
                    if (pendingFormData && params.automationId) {
                      // Close the template action dialog immediately
                      setShowTemplateActionDialog(false);
                      // Show the name input dialog
                      setShowCreateTemplateDialog(true);
                      // Keep pendingFormData for use after template creation
                      // It will be cleared in handleTemplateCreated
                    }
                  }}
                  className='ml-8 rounded-md bg-brand px-4 py-2 text-xs uppercase text-white shadow transition-all duration-300 hover:bg-brand-dark'
                >
                  OK
                </button>
              </div>
            </>
          ) : (
            <>
              <p className='mb-4 text-sm text-grey-50'>
                You&apos;ve made changes to the template. How would you like to
                save these changes?
              </p>

              <div className='space-y-3'>
                <button
                  type='button'
                  onClick={async () => {
                    if (pendingFormData && params.automationId) {
                      // Set flag to disable Prompt during update
                      setIsUpdatingAfterTemplateUpdate(true);

                      try {
                        const updatedData = await handleTemplateActionWrapper(
                          'update',
                          pendingFormData
                        );

                        // If template action failed, abort automation update
                        // handleTemplateAction already shows error toast, so we just abort here
                        // Keep dialog open to allow retry
                        if (!updatedData) {
                          // Don't close dialog - keep it open for retry
                          // Don't clear pendingFormData - preserve it for retry
                          return;
                        }

                        // Remember this template state so we don't re-open the
                        // template action dialog for the same template changes
                        // on subsequent saves.
                        lastTemplateFingerprintRef.current =
                          getTemplateFingerprint(updatedData);

                        // Clear pending form data and close dialog on success
                        setPendingFormData(null);
                        setShowTemplateActionDialog(false);
                        // Continue with automation update only if template update succeeded
                        // TypeScript: updatedData is guaranteed to be FormValues here (not null)
                        const convertedInput = formToInput(updatedData);
                        const res = await updateAutomation({
                          id: params.automationId,
                          input: convertedInput,
                        });

                        if (res.error) {
                          toast.error(
                            `Failed to update automation: ${
                              res.error.message || 'Unknown error'
                            }`
                          );
                          return;
                        }

                        const id = res.data?.updateAutomation;
                        if (id) {
                          // Navigate - Prompt should be disabled due to isUpdatingAfterTemplateUpdate flag
                          navigate(`/automations/${id}`);
                        }
                      } finally {
                        // Reset flag after navigation or error
                        setIsUpdatingAfterTemplateUpdate(false);
                      }
                    }
                  }}
                  className='w-full rounded border border-grey-20 bg-white p-4 pt-3 text-left hover:bg-grey-5'
                >
                  <div className='font-medium'>Update existing template</div>
                  <div className='mt-1 text-xs text-secondary'>
                    This affects all automations using this template.
                  </div>
                </button>

                <button
                  type='button'
                  onClick={async () => {
                    if (pendingFormData && params.automationId) {
                      // Close the template action dialog immediately
                      setShowTemplateActionDialog(false);
                      // Show the name input dialog
                      setShowCreateTemplateDialog(true);
                      // Keep pendingFormData for use after template creation
                      // It will be cleared in handleTemplateCreated
                    }
                  }}
                  className='w-full rounded border border-grey-20 bg-white p-4 pt-3 text-left hover:bg-grey-5'
                >
                  <div className='font-medium'>Create new template</div>
                  <div className='mt-1 text-xs text-secondary'>
                    The original template remains unchanged.
                  </div>
                </button>
              </div>
            </>
          )}
        </div>
      </Dialog>

      <AutomationForm
        data={{
          ...result.data,
          automation: automationWithCorrectTemplate,
        }}
        onSubmit={handleSubmitWithTemplateCheck}
        onCreateTemplate={handleCreateTemplate}
        externalShowCreateDialog={showCreateTemplateDialog}
        onCreateDialogShown={() => setShowCreateTemplateDialog(false)}
        onTemplateCreated={handleTemplateCreated}
        disablePrompt={
          isUpdatingAfterTemplateCreation || isUpdatingAfterTemplateUpdate
        }
      />
    </>
  );
}
